import { Component, OnInit, ViewChild } from '@angular/core';
import { RagCellBehavior } from '@app/directives/_layout/v-table/_components/cells/rag-cell/rag-cell.component';
import { IVTableColumn } from '@app/directives/_layout/v-table/_models/IVTableColumn';
import { IVTableRow } from '@app/directives/_layout/v-table/_models/IVTableRow';
import { VTableService } from '@app/directives/_layout/v-table/_services/v-table.service';
import { IAggregateFilterEvent, IVerticalFilterListItem } from '@app/directives/_resourceFilters/filter-bar/filter-bar.models';
import { VerticalFilterListComponent } from '@app/directives/_resourceFilters/vertical-filter-list/vertical-filter-list.component';
import { SortType } from '@app/enum/SortType';
import { VehicleType } from '@app/enum/VehicleType.enum';
import { FoundationNavigationModule } from '@app/main/modules/foundation/foundation.navigation';
import { DateService, DateUnit } from '@app/services/date.service';
import { NavigationService } from '@app/services/navigation.service';
import { TranslationService } from '@app/services/translation.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { finalize } from 'rxjs/operators';
import { DeviceHealthService } from '../../device-health.service';
import { DeviceHealthRecordType } from '../../enums/device-health-record-type';
import { SDCardErrorType } from '../../enums/sd-card-error-type';
import { DeviceHealthRecord } from '../../models/device-health-record';
import { DeviceHealthRecordsByVehicle } from '../../models/device-health-records-by-vehicle';
import './device-health-vehicle-view.component.scss';


@UntilDestroy()
@Component({
    selector: 'device-health-vehicle-view',
    template: require('./device-health-vehicle-view.component.html')
})
export class DeviceHealthVehicleViewComponent implements OnInit {
    private dayRange = 14;

    @ViewChild(VerticalFilterListComponent) verticalFilterList: VerticalFilterListComponent;

    loading = false;

    vehicleId: number;
    recordGroup: DeviceHealthRecordsByVehicle;

    tableColumns: IVTableColumn<DeviceHealthRecord>[] = [];
    tableRows: IVTableRow<DeviceHealthRecord>[] = [];
    filterItems: IVerticalFilterListItem<DeviceHealthRecord>[] = [];

    startDate: Date;
    endDate: Date;

    defaultDateRange = 3;
    selectedRecordTypes: DeviceHealthRecordType[] = [];

    exportTitle: string;

    get records(): DeviceHealthRecord[] {
        return this.recordGroup?.records ?? [];
    }

    get vehicleType(): string {
        return this.translationService.tEnum(VehicleType, "VehicleType", this.recordGroup.vehicleType, "");
    }

    constructor(
        private readonly navigationService: NavigationService,
        private readonly deviceHealthService: DeviceHealthService,
        private readonly vTableService: VTableService,
        private readonly translationService: TranslationService,
        private readonly dateService: DateService
    ) {}

    ngOnInit(): void {
        this.vehicleId = parseInt(this.navigationService.getRouteParam('vehicleId'));
        
        this.setDate();
        this.setQueryParams();
        this.createTable();
        this.loadData();
    }

    onFilterChanged(filterEvent: IAggregateFilterEvent<DeviceHealthRecord>) {
        if (filterEvent.trigger == "date") {
            this.startDate = filterEvent.date[0];
            this.endDate = filterEvent.date[1];

            this.loadData();
            return;
        }
    }

    goToVehicle(): void {
        this.navigationService.go(FoundationNavigationModule.VehicleView, {vehicleId: this.vehicleId})
    }

    private setQueryParams(): void {
        const queryParams = this.navigationService.getQueryParams();

        if (queryParams.filterBy) {
            this.selectedRecordTypes = (queryParams.filterBy as string).split(',').map(x => parseInt(x));
        }   
    }

    private setDate(): void {
        const now = new Date();
        const nowMinusIncludeDays = this.dateService.addOrSubtractTime(now, -this.dayRange, DateUnit.Day);

        this.endDate = this.dateService.getEndOfDay(now);
        this.startDate = this.dateService.getStartOfDay(nowMinusIncludeDays);
    }

    private loadData(): void {
        this.loading = true;

        this.deviceHealthService.getAllActiveAndHistoricalRecords(this.vehicleId, this.startDate, this.endDate)
        .pipe(
            untilDestroyed(this),
            finalize(() => {
                this.createTableRows();
                this.createFilterItems();
                this.setSelectedFilterItem();

                this.loading = false;
            })
        ).subscribe(recordGroup => {
            this.recordGroup = recordGroup;
            this.exportTitle = `${this.translationService.t('DeviceHealth.Vehicles')} - ${recordGroup.vehicleRegistration}`;
        })
    }

    private createTableRows(): void {
        this.tableRows = this.vTableService.createTableRows(this.records, this.tableColumns);
    }

    private createTable(): void {
        const regBehavior: RagCellBehavior<DeviceHealthRecord> = {
            redFn: (record) => !record.EndTime,
            grayFn: (record) => !!record.EndTime
        }

        this.tableColumns = [
            {
                header: this.vTableService.createTableHeader({ tKey: "DeviceHealth.Status", sortable: true, sorted: true, width: 140 }),
                createCellFunction: (record) => this.vTableService.createRagCell(this.getStatusLabel(record), record, regBehavior),
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Serial", sortable: true, width: 180 }),
                createCellFunction: record => this.vTableService.createTableCell(record.Serial)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Product", sortable: true }),
                createCellFunction: record => this.vTableService.createTableCell(record.productName)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Issue", sortable: true }),
                createCellFunction: record => this.vTableService.createTableCell(this.translationService.tEnum(DeviceHealthRecordType, "DeviceHealthRecordType", record.Type))
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "DeviceHealth.AdditionalInfo", sortable: true }),
                createCellFunction: record => this.vTableService.createTableCell(
                    !!record.SDCardErrorType ? this.translationService.tEnum(SDCardErrorType, "SDCardErrorType", record.SDCardErrorType) : "",
                    { tooltip: true })
            },
            {
                header: this.vTableService.createDateTableHeader({ tKey: "DeviceHealth.StartDate" }),
                createCellFunction: (record) => this.vTableService.createDateCell(record.StartTime, { dateTime: true }),
            },
            {
                header: this.vTableService.createDateTableHeader({ tKey: "DeviceHealth.EndDate" }),
                createCellFunction: (record) => this.vTableService.createDateCell(record.EndTime, { dateTime: true }),
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "DeviceHealth.Duration", sortType: SortType.Number, width: 160}),
                createCellFunction: (summary) => this.vTableService.createDurationCell(this.getDuration(summary), DateUnit.Ms)
            },
        ]
     }

    private getStatusLabel(record: DeviceHealthRecord): string {
        return record.EndTime
            ? this.translationService.translate("DeviceHealth.Ended")
            : this.translationService.translate("DeviceHealth.Active");
    }

    private createFilterItems(): void {
        this.filterItems = this.deviceHealthService.getFiltersForVehicleDetails(this.records);
    }

    private setSelectedFilterItem(): void {
        if (this.selectedRecordTypes.length > 0) {     
            this.filterItems
                .filter(item => this.selectedRecordTypes.includes(item.key))
                .forEach(item => this.verticalFilterList.selectItem(item));
        }
    }

    private getDuration(record: DeviceHealthRecord): number {
        const startTime = record.StartTime;
        const endTime = record.EndTime || new Date();

        return new Date(endTime).getTime() - new Date(startTime).getTime();
    }
}
